import { Input } from '@agms/ui'

export interface DejavooParamCatalogField {
  group: string
  param_name: string
  label: string
  control: 'textbox' | 'dropdown'
  options: string[]
  max_length: number | null
  help: string
  derive: string | null
}

export interface DejavooParamCatalogResponse {
  fields: DejavooParamCatalogField[]
  derived: Record<string, string>
  supported_combos: string[]
}

export interface DejavooFileBuilderState {
  values: Record<string, string>
  setFields: Record<string, boolean>
}

const GROUP_LABELS: Record<string, string> = {
  receipt_header: 'Receipt header',
  receipt_footer: 'Receipt footer',
  datawire: 'Datawire',
  batch: 'Batch',
  tax: 'Tax',
  surcharge: 'Surcharge',
}

const DATAWIRE_PARAM_NAMES = new Set(['FDO_DW_Merchant_ID', 'FDO_DW_Device_ID'])

interface DejavooFileBuilderProps {
  comboKey: string
  processor: 'omaha' | 'nashville'
  catalog: DejavooParamCatalogResponse | null
  loading: boolean
  error: string | null
  state: DejavooFileBuilderState
  onChange: (state: DejavooFileBuilderState) => void
}

export function emptyDejavooFileBuilderState(): DejavooFileBuilderState {
  return {
    values: {},
    setFields: {},
  }
}

export function initializeDejavooFileBuilderState(catalog: DejavooParamCatalogResponse): DejavooFileBuilderState {
  const values: Record<string, string> = {}
  const setFields: Record<string, boolean> = {}

  for (const field of catalog.fields) {
    values[field.param_name] = ''
    setFields[field.param_name] = false
  }

  return { values, setFields }
}

export function buildDejavooConfigPayload(
  fields: DejavooParamCatalogField[],
  state: DejavooFileBuilderState,
  processor: 'omaha' | 'nashville' = 'omaha',
): Record<string, string> {
  const payload: Record<string, string> = {}

  for (const field of visibleDejavooFields(fields, processor)) {
    const value = state.values[field.param_name] ?? ''
    if (state.setFields[field.param_name] && value.trim() !== '') {
      payload[field.param_name] = value
    }
  }

  return payload
}

export function validateDejavooFileBuilderState(
  fields: DejavooParamCatalogField[],
  state: DejavooFileBuilderState,
  processor: 'omaha' | 'nashville' = 'omaha',
): Record<string, string> {
  const errors: Record<string, string> = {}

  for (const field of visibleDejavooFields(fields, processor)) {
    if (!state.setFields[field.param_name]) {
      if (processor === 'omaha' && DATAWIRE_PARAM_NAMES.has(field.param_name)) {
        errors[field.param_name] = 'Required for Omaha.'
      }

      continue
    }

    const value = state.values[field.param_name] ?? ''
    if (processor === 'omaha' && DATAWIRE_PARAM_NAMES.has(field.param_name) && value.trim() === '') {
      errors[field.param_name] = 'Required for Omaha.'
      continue
    }

    if (field.max_length !== null && value.length > field.max_length) {
      errors[field.param_name] = `Must be ${field.max_length} characters or fewer.`
      continue
    }

    if (field.control === 'dropdown' && value !== '' && !field.options.includes(value)) {
      errors[field.param_name] = 'Select a valid option.'
      continue
    }

    if (field.param_name === 'Auto_Batch_Time' && value !== '' && !/^(?:[01]\d|2[0-3])[0-5]\d$/.test(value)) {
      errors[field.param_name] = 'Use HHMM format.'
    }
  }

  return errors
}

function visibleDejavooFields(
  fields: DejavooParamCatalogField[],
  processor: 'omaha' | 'nashville',
): DejavooParamCatalogField[] {
  return fields.filter((field) => processor === 'omaha' || field.group !== 'datawire')
}

export function DejavooFileBuilder({
  comboKey,
  processor,
  catalog,
  loading,
  error,
  state,
  onChange,
}: DejavooFileBuilderProps) {
  if (loading) {
    return (
      <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
        Loading custom file build fields...
      </p>
    )
  }

  if (error) {
    return (
      <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
        {error}
      </p>
    )
  }

  if (!catalog) {
    return null
  }

  if (!catalog.supported_combos.includes(comboKey)) {
    return (
      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        Custom file build isn't available for this combo yet — the terminal will be created from the standard template.
      </p>
    )
  }

  const fields = visibleDejavooFields(catalog.fields, processor)
  const errors = validateDejavooFileBuilderState(catalog.fields, state, processor)
  const groups = fields.reduce<Record<string, DejavooParamCatalogField[]>>((carry, field) => {
    carry[field.group] = [...(carry[field.group] ?? []), field]
    return carry
  }, {})

  const updateField = (field: DejavooParamCatalogField, value: string) => {
    onChange({
      values: {
        ...state.values,
        [field.param_name]: value,
      },
      setFields: {
        ...state.setFields,
        [field.param_name]: value !== '',
      },
    })
  }

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Custom file build</h3>
      </div>

      {Object.entries(groups).map(([group, fields]) => (
        <div key={group} className="space-y-3 rounded-md border border-gray-200 p-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {GROUP_LABELS[group] ?? group.replace(/_/g, ' ')}
          </h4>
          <div className="grid gap-4 md:grid-cols-2">
            {fields.map((field) => {
              const value = state.values[field.param_name] ?? ''
              const id = `dejavoo-${field.param_name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

              return (
                <div key={field.param_name}>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={id}>
                    {field.label}
                  </label>
                  {field.control === 'dropdown' ? (
                    <select
                      id={id}
                      value={value}
                      onChange={(event) => updateField(field, event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">No change</option>
                      {field.options.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id={id}
                      value={value}
                      maxLength={field.max_length ?? undefined}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  )}
                  {field.help && <p className="mt-1 text-xs text-gray-500">{field.help}</p>}
                  {errors[field.param_name] && <p className="mt-1 text-xs text-rose-700">{errors[field.param_name]}</p>}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </section>
  )
}
